import React, { useMemo, useState, useCallback, useEffect } from 'react';
import styled from 'styled-components';
import { Link, useParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { FaChevronLeft, FaChevronRight, FaTimes } from 'react-icons/fa';
import { getAppProjectBySlug } from '../../data/apps';
import { useLanguage } from '../../i18n';

const Page = styled.div`
  padding-top: 110px;
  padding-bottom: 80px;
  background: ${props => props.theme.colors.background};
`;

const Container = styled.div`
  max-width: 1040px;
  margin: 0 auto;
  padding: 0 24px;
`;

const HeroCard = styled.div`
  border-radius: ${props => props.theme.borderRadius.xl};
  padding: 26px;
  border: 1px solid rgba(59, 130, 246, 0.18);
  background:
    radial-gradient(1200px 500px at 20% 0%, rgba(59, 130, 246, 0.14), transparent 55%),
    radial-gradient(900px 450px at 100% 10%, rgba(16, 185, 129, 0.10), transparent 55%),
    ${props => props.theme.colors.white};
  box-shadow: ${props => props.theme.shadows.md};
  margin-bottom: 22px;
`;

const HeroGrid = styled.div`
  display: grid;
  grid-template-columns: 1.2fr 0.8fr;
  gap: 20px;
  align-items: start;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Breadcrumb = styled.div`
  margin-bottom: 18px;

  a {
    color: ${props => props.theme.colors.primary};
    text-decoration: none;
    font-weight: 600;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const Title = styled.h1`
  font-size: 40px;
  font-weight: 900;
  color: ${props => props.theme.colors.textDark};
  margin-bottom: 12px;

  @media (max-width: 768px) {
    font-size: 30px;
  }
`;

const Desc = styled.p`
  color: ${props => props.theme.colors.textLight};
  line-height: 1.8;
  font-size: 16px;
  margin-bottom: 18px;
`;

const Tags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
`;

const Tag = styled.span`
  font-size: 12px;
  padding: 6px 10px;
  border-radius: 999px;
  background: rgba(59, 130, 246, 0.08);
  color: ${props => props.theme.colors.primaryDark};
  border: 1px solid rgba(59, 130, 246, 0.18);
`;

const Preview = styled.div`
  border-radius: 18px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  background: ${props => props.theme.colors.backgroundLight};
  overflow: hidden;
  box-shadow: ${props => props.theme.shadows.sm};

  img {
    display: block;
    width: 100%;
    height: auto;
  }
`;

const Section = styled.section`
  margin-top: 26px;
  padding: 22px;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.xl};
  background: ${props => props.theme.colors.white};
  box-shadow: ${props => props.theme.shadows.sm};
`;

const SectionTitle = styled.h2`
  font-size: 16px;
  font-weight: 800;
  color: ${props => props.theme.colors.textDark};
  margin-bottom: 14px;
`;

const MiniCardsGrid = styled.div`
  display: grid;
  gap: 12px;
  margin-top: 16px;
  grid-template-columns: 1.15fr 0.85fr;
  grid-template-areas:
    "download extra"
    "download legal";

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    grid-template-areas:
      "download"
      "extra"
      "legal";
  }
`;

const MiniCard = styled.div<{ $area: 'download' | 'extra' | 'legal' }>`
  grid-area: ${p => p.$area};
  padding: 16px;
  border-radius: 16px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  background: rgba(255, 255, 255, 0.78);
  backdrop-filter: blur(8px);
  box-shadow: ${props => props.theme.shadows.sm};
`;

const MiniTitle = styled.div`
  font-size: 14px;
  font-weight: 900;
  color: ${props => props.theme.colors.textDark};
  margin-bottom: 10px;
`;

const ButtonRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`;

const PrimaryLinkButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 12px 16px;
  border-radius: 12px;
  background: ${props => props.theme.gradients.primary};
  color: white;
  font-weight: 800;
  text-decoration: none;
  border: none;
  box-shadow: ${props => props.theme.shadows.md};
  transition: all 0.25s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: ${props => props.theme.shadows.lg};
  }
`;

const SecondaryLinkButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 12px 16px;
  border-radius: 12px;
  background: rgba(59, 130, 246, 0.08);
  color: ${props => props.theme.colors.primaryDark};
  border: 1px solid rgba(59, 130, 246, 0.22);
  font-weight: 700;
  text-decoration: none;
  transition: all 0.25s ease;

  &:hover {
    transform: translateY(-1px);
    background: rgba(59, 130, 246, 0.12);
  }
`;

const DisabledButton = styled.button`
  padding: 12px 16px;
  border-radius: 12px;
  border: 1px dashed ${props => props.theme.colors.border};
  background: ${props => props.theme.colors.backgroundLight};
  color: ${props => props.theme.colors.textLight};
  font-weight: 700;
  cursor: not-allowed;
`;

const LegalLinks = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;

  a {
    color: ${props => props.theme.colors.primary};
    text-decoration: none;
    font-weight: 700;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const NotFound = styled.div`
  padding-top: 120px;
  padding-bottom: 80px;
  text-align: center;
  color: ${props => props.theme.colors.textLight};
`;

const ScreenshotGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const ScreenshotButton = styled.button`
  padding: 0;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 14px;
  overflow: hidden;
  background: ${props => props.theme.colors.backgroundLight};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: ${props => props.theme.shadows.md};
    border-color: rgba(59, 130, 246, 0.25);
  }

  img {
    display: block;
    width: 100%;
    height: 160px;
    object-fit: cover;
  }
`;

const ModalOverlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.68);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  z-index: 2000;
`;

const ModalContent = styled(motion.div)`
  width: min(1060px, 100%);
  background: rgba(12, 12, 12, 0.9);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 18px;
  overflow: hidden;
  position: relative;
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.55);
`;

const ModalToolbar = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  display: flex;
  gap: 10px;
  z-index: 3;
`;

const IconButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  background: rgba(255, 255, 255, 0.08);
  color: white;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.14);
  }
`;

const ModalImageWrap = styled.div`
  padding: 56px 16px 18px;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 100%;
    height: auto;
    max-height: calc(80vh - 56px);
    object-fit: contain;
    border-radius: 12px;
  }
`;

const ModalNav = styled.div`
  position: absolute;
  top: 50%;
  left: 12px;
  right: 12px;
  transform: translateY(-50%);
  display: flex;
  justify-content: space-between;
  pointer-events: none;
  z-index: 2;

  button {
    pointer-events: auto;
  }
`;

function AppScreenshotPicture({
  s,
  className,
  loading,
}: {
  s: { src: string; alt: string };
  className?: string;
  loading?: 'eager' | 'lazy';
}) {
  return (
    <img className={className} src={s.src} alt={s.alt} loading={loading} />
  );
}

const AppDetailPage: React.FC = () => {
  const { slug } = useParams();
  const { t } = useLanguage();

  const app = useMemo(() => getAppProjectBySlug(slug), [slug]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(0);

  const screenshots = app?.screenshots ?? [];
  const openPreview = useCallback((idx: number) => {
    setPreviewIndex(idx);
    setPreviewOpen(true);
  }, []);

  const closePreview = useCallback(() => setPreviewOpen(false), []);

  const prev = useCallback(() => {
    if (screenshots.length === 0) return;
    setPreviewIndex(i => (i - 1 + screenshots.length) % screenshots.length);
  }, [screenshots.length]);

  const next = useCallback(() => {
    if (screenshots.length === 0) return;
    setPreviewIndex(i => (i + 1) % screenshots.length);
  }, [screenshots.length]);

  useEffect(() => {
    if (!previewOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closePreview();
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [previewOpen, closePreview, prev, next]);

  useEffect(() => {
    // 切换不同 app 时关闭预览
    setPreviewOpen(false);
    setPreviewIndex(0);
  }, [slug]);

  if (!app) {
    return (
      <NotFound>
        <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 10 }}>{t('apps.detail.notFound')}</div>
        <Link to="/apps">{t('apps.detail.backToFactory')}</Link>
      </NotFound>
    );
  }

  const termsTo = `/terms?app=${encodeURIComponent(app.slug)}`;
  const privacyTo = `/privacy?app=${encodeURIComponent(app.slug)}`;

  return (
    <Page>
      <Container>
        <Breadcrumb>
          <Link to="/apps">{t('apps.detail.backToFactory')}</Link>
        </Breadcrumb>

        <HeroCard>
          <HeroGrid>
            <div>
              <Title>{app.name}</Title>
              <Desc>{app.description ?? app.shortDescription}</Desc>

              {!!app.tags?.length && (
                <Tags>
                  {app.tags.slice(0, 8).map(tag => (
                    <Tag key={tag}>{tag}</Tag>
                  ))}
                </Tags>
              )}

              <MiniCardsGrid>
                <MiniCard $area="download">
                  <MiniTitle>{t('apps.detail.downloadTitle')}</MiniTitle>
                  <ButtonRow>
                    {app.downloads.ios?.url ? (
                      <PrimaryLinkButton href={app.downloads.ios.url} target="_blank" rel="noopener noreferrer">
                        {app.downloads.ios.label}
                      </PrimaryLinkButton>
                    ) : app.downloads.ios ? (
                      <DisabledButton>{app.downloads.ios.label}</DisabledButton>
                    ) : null}

                    {app.downloads.android?.url ? (
                      <PrimaryLinkButton href={app.downloads.android.url} target="_blank" rel="noopener noreferrer">
                        {app.downloads.android.label}
                      </PrimaryLinkButton>
                    ) : app.downloads.android ? (
                      <DisabledButton>{app.downloads.android.label}</DisabledButton>
                    ) : null}

                    {app.downloads.web?.url ? (
                      <SecondaryLinkButton href={app.downloads.web.url} target="_blank" rel="noopener noreferrer">
                        {app.downloads.web.label}
                      </SecondaryLinkButton>
                    ) : null}
                  </ButtonRow>
                </MiniCard>

                <MiniCard $area="extra">
                  <MiniTitle>{t('apps.detail.extraLinksTitle')}</MiniTitle>
                  <ButtonRow>
                    {app.externalLinks?.manageSubscription?.ios ? (
                      <SecondaryLinkButton
                        href={app.externalLinks.manageSubscription.ios}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {t('apps.detail.manageSubscription.ios')}
                      </SecondaryLinkButton>
                    ) : null}
                    {app.externalLinks?.manageSubscription?.android ? (
                      <SecondaryLinkButton
                        href={app.externalLinks.manageSubscription.android}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {t('apps.detail.manageSubscription.android')}
                      </SecondaryLinkButton>
                    ) : null}
                  </ButtonRow>
                </MiniCard>

                <MiniCard $area="legal">
                  <MiniTitle>{t('apps.detail.legalTitle')}</MiniTitle>
                  <LegalLinks>
                    <Link to={termsTo}>{t('legal.terms')}</Link>
                    <Link to={privacyTo}>{t('legal.privacy')}</Link>
                  </LegalLinks>
                </MiniCard>
              </MiniCardsGrid>
            </div>

            {screenshots[0]?.src ? (
              <Preview>
                <AppScreenshotPicture s={screenshots[0]} loading="eager" />
              </Preview>
            ) : null}
          </HeroGrid>
        </HeroCard>

        {!!screenshots.length && (
          <Section>
            <SectionTitle>{t('apps.detail.screenshotsTitle')}</SectionTitle>
            <ScreenshotGrid>
              {screenshots.map((s, idx) => (
                <ScreenshotButton
                  key={s.src}
                  onClick={() => openPreview(idx)}
                  aria-label={`${t('apps.detail.screenshots.open')} ${idx + 1}`}
                >
                  <AppScreenshotPicture s={s} loading="lazy" />
                </ScreenshotButton>
              ))}
            </ScreenshotGrid>
          </Section>
        )}
      </Container>

      <AnimatePresence>
        {previewOpen && screenshots[previewIndex] ? (
          <ModalOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closePreview}
          >
            <ModalContent
              initial={{ opacity: 0, scale: 0.98, y: 8 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.98, y: 8 }}
              transition={{ duration: 0.18 }}
              onClick={e => e.stopPropagation()}
            >
              <ModalToolbar>
                <IconButton onClick={closePreview} aria-label="关闭预览">
                  <FaTimes />
                </IconButton>
              </ModalToolbar>

              {screenshots.length > 1 ? (
                <ModalNav>
                  <IconButton onClick={prev} aria-label="上一张">
                    <FaChevronLeft />
                  </IconButton>
                  <IconButton onClick={next} aria-label="下一张">
                    <FaChevronRight />
                  </IconButton>
                </ModalNav>
              ) : null}

              <ModalImageWrap>
                <AppScreenshotPicture s={screenshots[previewIndex]} loading="eager" />
              </ModalImageWrap>
            </ModalContent>
          </ModalOverlay>
        ) : null}
      </AnimatePresence>
    </Page>
  );
};

export default AppDetailPage;

